"use client";

import React, {useCallback, useEffect, useState} from "react"
import Image from "next/image";
import {Locale} from "@/i18n-config";
import type {Slider as SliderProps} from "@docid/types/src/payload-types";
import {EmblaCarouselType, EmblaOptionsType} from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay, {AutoplayOptionsType} from "embla-carousel-autoplay";
import {GoDot, GoDotFill} from "react-icons/go";
import clsx from "clsx";
import {getMediaBySize, isMedia} from "@/utils/content/media";

type Props = {
    lang?: Locale;
    dictionary?: any;
    className?: string;
    block: SliderProps;
    position?: number;
}

export function Slider({ lang, dictionary, className, block, position } : Props) {

    const options: EmblaOptionsType = {
        loop: true,
        align: "center",
    }

    const optionsAutoplay: AutoplayOptionsType = {
        playOnInit: true,
        delay: 6000,
    }

    const [emblaRef, emblaApi] = useEmblaCarousel(options, [
        Autoplay(optionsAutoplay)
    ]);

    const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);

    return (
        <div className={clsx("relative", className)}>
            <div className="absolute z-[9999] w-full flex justify-center items-center">
                <div
                    className="flex flex-row justify-center items-center text-zinc-900 bg-white/40 py-1 px-2 rounded-full">
                    {scrollSnaps.map((_, index) => (
                        index === selectedIndex ? (
                            <button key={`dot-${index}`} onClick={() => onDotButtonClick(index)}>
                                <GoDotFill size={30}/>
                            </button>
                        ) : (
                            <button key={`dot-${index}`} onClick={() => onDotButtonClick(index)}>
                                <GoDot size={30}/>
                            </button>
                        )
                    ))}
                </div>
            </div>
            <div ref={emblaRef} className="pt-20 overflow-hidden">
                <div className="flex backface-hidden touch-pan-y touch-pinch-zoom -ml-5 sm:-ml-10 lg:-ml-15">
                    {block.slides && block.slides.map((slide, index) => (
                        <div key={`${slide.id}-${index}`}
                             className="flex-shrink-0 sm:flex-[0_0_100%] lg:flex-[0_0_calc(100%/3)] max-w-full md:max-w-[calc(100%/3)] pl-5 sm:pl-10 lg:pl-15 text-center cursor-grab">
                            {slide.image && isMedia(slide.image) && (
                                <div className="flex items-center justify-center h-[175px] select-none">
                                    {(() => {
                                        const image = getMediaBySize(slide.image, "medium");
                                        return (
                                            <Image
                                                loading="eager"
                                                className="max-w-full max-h-[175px] select-none"
                                                width={image.width}
                                                height={image.height}
                                                src={image.url}
                                                alt={image.alt}
                                                priority={!!(position && position <= 2)}
                                            />
                                        );
                                    })()}
                                </div>
                            )}
                            <div className="flex flex-grow-0 flex-col gap-2 mt-6">
                                {slide.title &&
                                    <h3 className="font-semibold text-xl md:text-2xl select-none">{slide.title}</h3>}
                                {slide.description &&
                                    <p className="font-source font-normal text-lg md:text-xl select-none leading-normal mt-2">{slide.description}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export type UseDotButtonType = {
    selectedIndex: number
    scrollSnaps: number[]
    onDotButtonClick: (index: number) => void
}

export const useDotButton = (
    emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

    const onDotButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return
            emblaApi.scrollTo(index)
        },
        [emblaApi]
    )

    const onInit = useCallback((emblaApi: EmblaCarouselType) => {
        setScrollSnaps(emblaApi.scrollSnapList())
    }, [])

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap())
    }, [])

    useEffect(() => {
        if (!emblaApi) return
        onInit(emblaApi)
        onSelect(emblaApi)
        emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect)
    }, [emblaApi, onInit, onSelect])

    return {
        selectedIndex,
        scrollSnaps,
        onDotButtonClick
    }

}